import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWrapRequest } from "utils";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import CartProduct from "components/Customize/CartProduct";
import CartInfo from "components/Customize/CartInfo";
import VideoExpansionPackModal from "components/Customize/VideoExpansionPackModal";
import { getCustomizePage } from "services/get-customizePage";
import { getGetProductContentPage } from "services/get-getProductContent";
import { getPromotionContent } from "services/get-getPromotionContent";
import { postCustomizePage } from "services/post-customizePage";
import { getPremiumWifiPage } from "services/get-premiumWifiPage";
import { postModalAd } from "services/post-modalAd";
import { getVideoExpansionPackPage } from "services/get-videoExpansionPackPage";
import { postAddVideoExpansionServiceOption } from "services/post-addVideoExpansionServiceOption";
import { postUpdateCartServiceOption } from "services/post-updateCartServiceOption";
import { postRemoveFromCart } from "services/post-removeFromCart";
import { getCart } from "services/get-getCart";
import { postUpgradeProduct } from "services/post-upgradeProduct";
import { postDowngradeProduct } from "services/post-downgradeProduct";
import { useRecoilState_TRANSITION_SUPPORT_UNSTABLE as useRecoilState, useSetRecoilState } from "recoil";
import { cartState, mobileRegistryModalState, scheduleState, showAdvertisingModalState, advertisingModalDataState, adActionsState } from "atoms";
import { gtmCheckIfExistingCustomer } from "utils/googleTagManager";
import { emptyScheduleState, PATH } from "utils/const";
import "components/Customize/customize.scss";
import AdvertisingModal from "components/Customize/AdvertisingModal";
import { CartModel } from "atoms/models";

interface ProductMap {
  [productId: number]: object;
}

function Customize() {
  const wrapRequest = useWrapRequest();
  const navigate = useNavigate();

  const [cart, setCart] = useRecoilState(cartState);
  const [showMobileRegistryModal, setShowMobileRegistryModal] = useRecoilState(mobileRegistryModalState);
  const [products, setProducts] = useState<any>(null);
  const [promotion, setPromotion] = useState<any>(null);
  const [videoExpansionPackData, setVideoExpansionPackData] = useState<any>(null);
  const [showAdvertisingModal, setShowAdvertisingModal] = useRecoilState(showAdvertisingModalState);
  const setAdActions = useSetRecoilState(adActionsState);
  const setAdvertisingModalData = useSetRecoilState(advertisingModalDataState);
  const setSchedule = useSetRecoilState(scheduleState);

  const getProductMap = async (cart: CartModel) => {
    const productMap: ProductMap = {};
    if (cart && cart.list) {
      for (let i = 0; i < cart.list.cartProducts.length; i++) {
        const product = cart.list.cartProducts[i];
        const response = await getGetProductContentPage(product.productId, "customize");
        productMap[product.productId] = response;
      }
    }
    return productMap;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      wrapRequest(async () => {
        const responseBody = await getCustomizePage();
        if (responseBody._links?.["ec:windowLocation"]?.href) {
          return navigate(responseBody._links["ec:windowLocation"].href);
        }
        const cartObject = responseBody._embedded["ec:cart"];
        const productMap = await getProductMap(cartObject);
        if (cartObject && cartObject.bundle) {
          const promotionResponse = await getPromotionContent(cartObject.bundle.id, "customize");
          setPromotion(promotionResponse.contentItemEcommerce);
        }
        setCart(cartObject);
        setProducts(productMap);
        gtmCheckIfExistingCustomer(cartObject);
      });
    };
    fetchInitialData();
    // eslint-disable-next-line
  }, [navigate, setCart]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const onContinue = () => {
    const onContinueAsync = async () => {
      const response = await postCustomizePage();
      if (
        response.ecommerceAction === "prompt" &&
        response.ecommerceMessageType === "modal" &&
        response["_embedded"]?.["ec:ad"] &&
        response["_embedded"]?.["ec:adActions"]
      ) {
        await getPremiumWifiPage();
        setAdvertisingModalData(response["_embedded"]["ec:ad"]);
        setShowAdvertisingModal(true);
        setAdActions(response["_embedded"]["ec:adActions"]);
      } else if (response.ecommerceAction === "error") {
        throw response.message;
      } else {
        if (response._links?.["ec:windowLocation"]?.href) {
          navigate(response._links["ec:windowLocation"].href);
        }
      }
    };
    wrapRequest(onContinueAsync, {
      afterFinally: () => {
        setShowMobileRegistryModal(false);
      },
    });
  };

  const onChangeServiceOptionAmount = (cartServiceOptionId: number, selectedQuantity: number) => {
    wrapRequest(async () => {
      const updateResponse = await postUpdateCartServiceOption(cartServiceOptionId, selectedQuantity);
      if (updateResponse.ecommerceResponse?.ecommerceAction === "continue") {
        const cartResponse = await getCart();
        setCart(cartResponse);
      } else if (
        updateResponse.ecommerceResponse?.ecommerceAction === "prompt" &&
        updateResponse.ecommerceResponse?.ecommerceMessageType === "modal_camera"
      ) {
        const videoExpansionPackResponse = await getVideoExpansionPackPage();
        if (videoExpansionPackResponse.ecommerceAction === "continue") {
          setVideoExpansionPackData({
            ...videoExpansionPackResponse._embedded["ec:modalData"],
            cartServiceOptionId,
            selectedQuantity,
          });
        } else if (videoExpansionPackResponse.ecommerceAction === "error") {
          throw videoExpansionPackResponse.message;
        }
      } else if (updateResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw updateResponse.ecommerceResponse?.message;
      }
    });
  };

  const promotionSubHeading = promotion?.contentEcommerce?.find((item: { fieldName: string }) => item.fieldName === "subHeading");

  const onRemoveProduct = (itemId: number) => {
    wrapRequest(async () => {
      const removeResponse = await postRemoveFromCart(itemId, false, false);
      if (removeResponse.ecommerceAction === "continue") {
        const cartResponse = await getCart();
        setCart(cartResponse);
        if (cartResponse.list?.cartProducts?.length === 0) {
          navigate(PATH.BROWSE);
        }
      } else if (removeResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw removeResponse.ecommerceResponse?.message;
      }
    });
  };

  const removeBundleFromCart = () => {
    wrapRequest(async () => {
      const removeResponse = await postRemoveFromCart(cart.bundle.id, true, false);
      if (removeResponse?.ecommerceAction === "continue") {
        const cartResponse = await getCart();
        setCart(cartResponse);
        setPromotion(null);
        if (cartResponse.list?.cartProducts?.length === 0) {
          navigate(PATH.BROWSE);
        }
      } else if (removeResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw removeResponse.ecommerceResponse?.message;
      }
    });
  };

  const refreshCart = async () => {
    const cartResponse = await getCart();
    const productMap = await getProductMap(cartResponse);
    if (cartResponse.bundle) {
      const promotionResponse = await getPromotionContent(cartResponse.bundle.id, "customize");
      setPromotion(promotionResponse.contentItemEcommerce);
    }
    setCart(cartResponse);
    setProducts(productMap);
  };

  const onUpgradeProduct = (cartProductId: number, upgradeProductId: number) => {
    wrapRequest(async () => {
      const upgradeProductResponse = await postUpgradeProduct(cartProductId, null, upgradeProductId);
      if (upgradeProductResponse.ecommerceResponse?.ecommerceAction === "continue") {
        await refreshCart();
      } else if (upgradeProductResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw upgradeProductResponse.ecommerceResponse?.message;
      }
    });
  };

  const onDowngradeProduct = (upgradeCartProductId: number) => {
    wrapRequest(async () => {
      const downgradeResponse = await postDowngradeProduct(upgradeCartProductId);
      if (downgradeResponse.ecommerceResponse?.ecommerceAction === "continue") {
        await refreshCart();
      } else if (downgradeResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw downgradeResponse.ecommerceResponse?.message;
      }
    });
  };

  const headerLine = (
    <>
      <Row className="mt-5 pt-5">
        <Col className="step-title">Customize Order</Col>
      </Row>
      <Row>
        <Col className="step-title-divider"></Col>
      </Row>
    </>
  );

  if (cart === null || Object.keys(cart).length === 0 || products === null) {
    return <Container style={{ height: "30vh" }}>{headerLine}</Container>;
  }

  return (
    <Container className="px-0">
      {headerLine}
      <div className="pt-5 pb-5 customize-content">
        <div>
          {promotionSubHeading && (
            <Row>
              <div className="d-flex mb-3">
                <div className="ps-3">
                  <span className="h5 promotion-sub-heading">{promotionSubHeading.content}</span>
                  <Button
                    variant="link"
                    className="ps-0 d-block d-md-inline-block text-decoration-none"
                    onClick={() => {
                      removeBundleFromCart();
                      setSchedule(emptyScheduleState);
                    }}
                  >
                    <small>Remove from cart</small>
                  </Button>
                </div>
                <span style={{ flex: 1 }}></span>
                <span className="h5 ps-1 pe-3 fw-bold">${cart.bundle.price}</span>
              </div>
            </Row>
          )}
          {cart.list?.cartProducts?.map((product: { productId: number; promotionId: number; id: number }) => (
            <CartProduct
              key={product.id}
              product={product}
              productDefinition={products[product.productId]}
              enableRemoval={product.promotionId == null || product.promotionId === 0}
              onRemoveProduct={onRemoveProduct}
              onChangeServiceOptionAmount={onChangeServiceOptionAmount}
              onUpgradeProduct={onUpgradeProduct}
              onDowngradeProduct={onDowngradeProduct}
            />
          ))}
          <div className="continue-button-bottom">
            <div className="step-title-divider mt-2 mb-4"></div>
            <Container>
              <div className="customize-contine-button-container">
                <div className="pb-3 text-md-start">
                  <a className="continue-shopping-link-a-tag text-decoration-none" href={PATH.BROWSE}>
                    <i style={{ fontSize: "x-large" }} className="text-pastel-light-blue me-2 fa-sharp fa-solid fa-chevron-left"></i>
                    <span className="text-pastel-light-blue align-top fw-bold h5">Continue Shopping</span>
                  </a>
                </div>
                <div className="text-end">
                  <Button
                    onClick={onContinue}
                    className="cart-registry-button w-100 text-center no-radius pt-3 fw-bold"
                    style={{ paddingBottom: "39px" }}
                  >
                    <strong className="px-5">Continue</strong>
                  </Button>
                </div>
              </div>
            </Container>
          </div>
        </div>
        <div className="customize-register">
          <CartInfo cart={cart} onContinue={onContinue} />
          <AdvertisingModal
            show={showAdvertisingModal}
            handleClose={() => setShowAdvertisingModal(false)}
            handleDecision={(actions) => {
              wrapRequest(async () => {
                let response = await postModalAd(actions);
                setShowAdvertisingModal(false);
                if (response._links["ec:windowLocation"].href) {
                  navigate(response._links["ec:windowLocation"].href);
                }
              });
            }}
          />
          <VideoExpansionPackModal
            show={videoExpansionPackData !== null}
            handleClose={() => setVideoExpansionPackData(null)}
            data={videoExpansionPackData}
            handleAccept={(cartServiceOptionId, selectedQuantity, options = {}) => {
              wrapRequest(async () => {
                const response = await postAddVideoExpansionServiceOption(cartServiceOptionId, selectedQuantity.toString());
                if (response.ecommerceResponse?.ecommerceAction === "continue") {
                  const cartResponse = await getCart();
                  setCart(cartResponse);
                  setVideoExpansionPackData(null);
                } else if (response.ecommerceResponse?.ecommerceAction === "error") {
                  setVideoExpansionPackData(null);
                  throw response.ecommerceResponse?.message;
                }
              }, options);
            }}
          />
          <Modal show={showMobileRegistryModal} onHide={() => setShowMobileRegistryModal(false)}>
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>{Object.keys(cart).length > 1 ? <CartInfo cart={cart} onContinue={onContinue} /> : null}</Modal.Body>
          </Modal>
        </div>
      </div>
    </Container>
  );
}

export default Customize;
